<!--
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
* The page body lives in the #pageBody template below and is projected into the standard form
* page; config.fieldSet.formLayout is emptied so the standard field-set layout renders nothing.
*
* Sections are authored in phone reading order; the .scss grid re-flows them into a 3- or
* 4-column shell on wider screens via named grid areas.
-->
<app-form-page [config]="config" [dialogRef]="dialogRef">
    <ng-container [ngTemplateOutlet]="pageBody"></ng-container>
</app-form-page>


<ng-template #pageBody>
    <div class="proto">

        <section class="sec sec-brief">
            <h3 class="sec-head">
                <span i18n>Summary</span>
                <app-section-action-button i18n-tip tip="Edit the title and description"
                    testId="summary" (act)="editSummary()"></app-section-action-button>
            </h3>
            <!-- read-only glimpse of the title/description; the Edit button above opens a real edit -->
            <app-text-edit label="Title" testId="request-title"
                [textValue]="titleField.control?.value"></app-text-edit>
            <app-text-edit class="proto-description" label="Description" testId="request-description"
                [isHtml]="true"[textValue]="descriptionField.control?.value"></app-text-edit>
        </section>

        <div class="rail rail-l">
            <section class="sec sec-details">
                <h3 class="sec-head"><span i18n>Details</span>
                    <app-section-action-button i18n-tip tip="Edit the request details"
                        testId="details" (act)="editDetails()"></app-section-action-button>
                </h3>
                <app-ctl-holder [control]="refField.control"></app-ctl-holder>
                <app-ctl-holder [control]="teamField.control"></app-ctl-holder>
                <app-ctl-holder [control]="agentField.control"></app-ctl-holder>
                <app-ctl-holder [control]="sourceField.control"></app-ctl-holder>
                <app-ctl-holder [control]="requestTypeField.control"></app-ctl-holder>
                <app-ctl-holder [control]="statusPillField.control"></app-ctl-holder>
                <app-ctl-holder [control]="actionStatusPillField.control"></app-ctl-holder>
                <app-ctl-holder [control]="prioField.control"></app-ctl-holder>
                @if (showAssistant(sr)) {
                    <app-ctl-holder [control]="aiField.control"></app-ctl-holder>
                }
                <app-ctl-holder [control]="srTopicField.control"></app-ctl-holder>
                <app-ctl-holder [control]="srClassField.control"></app-ctl-holder>
                <app-ctl-holder [control]="billableField.control"></app-ctl-holder>
                <app-ctl-holder [control]="parentField.control"></app-ctl-holder>
            </section>

            <section class="sec sec-approval">
                <h3 class="sec-head"><span i18n>Escalation &amp; Approval</span>
                    <app-section-action-button i18n-tip tip="Edit the channel and approval"
                        testId="approval" (act)="editApproval()"></app-section-action-button>
                </h3>
                @if (sr) {
                    <app-request-approval-tile [sr]="sr" (changed)="reloadFrom($event)"></app-request-approval-tile>
                }
            </section>
        </div>

        <div class="rail rail-r">
            <section class="sec sec-dates">
                <h3 class="sec-head" i18n>Dates</h3>
                <app-ctl-holder [control]="createdField.control"></app-ctl-holder>
                <app-ctl-holder [control]="completedField.control"></app-ctl-holder>
                <app-ctl-holder [control]="ownerReadField.control"></app-ctl-holder>
                <app-ctl-holder [control]="lastUpdateField.control"></app-ctl-holder>
            </section>

            <section class="sec sec-people">
                <h3 class="sec-head"><span i18n>People</span>
                    <app-section-action-button i18n-tip tip="Edit the people on this request"
                        testId="people" (act)="editPeople()"></app-section-action-button>
                </h3>
                <app-ctl-holder [control]="ownedByField.control"></app-ctl-holder>
                <app-ctl-holder [control]="resolvedByField.control"></app-ctl-holder>
                <app-ctl-holder [control]="reportedByField.control"></app-ctl-holder>
                <app-ctl-holder [control]="personField.control"></app-ctl-holder>
                <app-ctl-holder [control]="unitField.control"></app-ctl-holder>
                <app-ctl-holder [control]="areaField.control"></app-ctl-holder>
                <app-ctl-holder [control]="coreField.control"></app-ctl-holder>
                @if (propertyManagerField.visible.form) {
                    <app-ctl-holder [control]="propertyManagerField.control"></app-ctl-holder>
                }
            </section>

            @if (isWorkOrder) {
                <section class="sec sec-workorder">
                    <h3 class="sec-head"><span i18n>Work Order</span>
                        <app-section-action-button i18n-tip tip="Edit the work order"
                            testId="workOrder" (act)="editWorkOrder()"></app-section-action-button>
                    </h3>
                    <app-ctl-holder [control]="supplierField.control"></app-ctl-holder>
                    <div class="wo-sub" i18n>Estimated</div>
                    @for (rb of sr?.requestBudget ?? []; track rb.id) {
                        <div class="wo-row">
                            <span class="condensed-label">{{ rb.bCodeName }}</span>
                            <span class="wo-amount">{{ rb.amount | currency:'EUR' }}</span>
                        </div>
                    } @empty {
                        <div class="wo-empty" i18n>Edit to enter planned costs</div>
                    }

                    <div class="wo-sub" i18n>Actual</div>
                    @for (txn of sr?.transactions ?? []; track txn.id) {
                        <div class="wo-row">
                            <a class="wo-link" [routerLink]="txnLink(txn)">
                                {{ txnTypeName(txn) }} {{ txn.refNr }}
                            </a>
                            <span class="wo-amount">{{ txnAmount(txn) | currency:'EUR' }}</span>
                        </div>
                    } @empty {
                        <div class="wo-empty" i18n>No invoices or accruals raised yet</div>
                    }
                </section>
            }
        </div>

        <section class="sec sec-attach">
            <app-ctl-holder [control]="attachmentGrid.control"></app-ctl-holder>
        </section>

        <section class="sec sec-actions">
            <app-ctl-holder [control]="actionGrid.control"></app-ctl-holder>
        </section>

        <section class="sec sec-kids">
            <app-ctl-holder [control]="childRequestsField.control"></app-ctl-holder>
        </section>

        <section class="sec sec-comments">
            <h3 class="sec-head" i18n>Comments &amp; Followers</h3>
            <app-ctl-holder [control]="chatField.control"></app-ctl-holder>
        </section>

    </div>
</ng-template>
